import { Button, Form, Space, UploadFile, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  ExclamationCircleFilled,
  HomeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import UsersForm, { UserFormValues } from "../Form";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import {
  UserCreateParams,
  UserUpdateParams,
  useUserDelete,
  useUserGetById,
  useUserUpdate,
} from "../../../../hooks/api/users";
import { useNavigate, useParams } from "react-router";
import { User, UserRoleType } from "../../../../store/auth";
import { UserStatus } from "../../../../enums/user";
import { generateFileObjFromUrl } from "../../../Common/ImageUpload";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { useSimpleModal } from "../../../Common/Modal/Simple";
import DeleteButton from "../../../Common/DeleteButton";
import { Role } from "../../../../enums/user";

const initialState: UserFormValues = {
  fullName: "",
  email: "",
  avatar: "",
  role: Role.GUEST,
  plan: "",
  birthdate: null,
  gender: "",
  address: "",
  country: "",
  city: "",
  zipCode: "",
  phone: "",
};

const prepareValues = (data: User): UserCreateParams => ({
  ...initialState,
  ...data,
  birthdate: data.birthdate ? dayjs(data.birthdate) : null,
});

export default function UsersEditContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState(initialState);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { open, contextHolder } = useSimpleModal();
  const userById = useUserGetById(id);
  const userUpdate = useUserUpdate();
  const userDelete = useUserDelete(id);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: t("routes.users"), href: "/users" },
      { title: t("common.edit") },
    ]);
  }, [i18n.language, t]);

  useEffect(() => {
    if (userById.data && !userById.loading && !userById.error) {
      const values = prepareValues(userById.data);
      setInitialValues(values);
      form.setFieldsValue(values);
      if (values.avatar) {
        setFileList([generateFileObjFromUrl(values.avatar)]);
      }
    }
  }, [userById.data, userById.loading, userById.error]);

  useEffect(() => {
    form.setFieldValue("avatar", fileList[0]?.url || "");
  }, [fileList]);

  useEffect(() => {
    if (userUpdate.data) {
      navigate(`/users/`);
    }
  }, [userUpdate]);

  useEffect(() => {
    if (!userDelete.error && !userDelete.loading && userDelete.data) {
      navigate(`/users/`);
    }
  }, [userDelete.error, userDelete.loading, userDelete.data]);

  /**
   * Deleting the user. The same dialog as before, asked from a button in the
   * open rather than from an item in an ellipsis menu: an action that cannot be
   * taken back should not be something the user finds by looking for it.
   */
  const confirmDelete = () =>
    open({
      icon: <ExclamationCircleFilled />,
      title: t("pages.users.deleteUserTitle"),
      content: (
        <span>
          {t("pages.users.deleteUser")} <b>{userById.data?.fullName || "-"}</b>?
        </span>
      ),
      cancelText: t("common.cancel"),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      onOk: () => userDelete.fetch(),
    });

  const handleSubmit = async (values: UserUpdateParams) => {
    form.validateFields().then(() => {
      const payload: UserUpdateParams = {
        ...values,
        avatar: fileList.length > 0 ? fileList[0].url : "",
        zipCode: values.zipCode ?? "",
      };
      userUpdate.fetch({ ...payload }, id);
    });
  };

  useMessageSuccess([userUpdate], "Updates saved.");
  useMessageError([userUpdate]);

  return (
    <SinglePageContainer
      title={t("pages.users.userEdit")}
      extra={
        // One group, spaced the way every group of actions in the app is —
        // the page header's own wrapper spaces its children by 12, and Delete
        // measures its distance from 8.
        <Space>
          <DeleteButton onClick={confirmDelete} />

          {/* Every action this user has, in the place the user is edited: the
              row in the list and the preview offer the same set, so none of the
              three is where something is hidden. */}
          {userById.data?.status === UserStatus.PENDING ? (
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                // TODO(design): nothing is sent. The invitation endpoint is a
                // developer's to connect — see DEVELOPER_TASKS.md.
                message.success(
                  t("pages.users.invitationResent", {
                    name: userById.data?.fullName || userById.data?.email,
                  }),
                )
              }
            >
              {t("pages.users.resendInvitation")}
            </Button>
          ) : null}

          <Button type="primary" onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </Space>
      }
    >
      <div>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleSubmit}
        >
          <UsersForm fileList={fileList} setFileList={setFileList} />
        </Form>
      </div>
      {contextHolder}
    </SinglePageContainer>
  );
}
